// @ts-strict-ignore
import type React from 'react';
import type { useSnackBar } from '@usealloy/component-library';
import {
  getTextColorForBackground,
  IconName,
} from '@usealloy/component-library';

import type { Action } from 'flow/store/engine/types';
import type { DropdownItem } from 'flow/editor/components/Graph/nodes/BaseNode/NodeDropdownMenu';
import { /* OnCopyDefaults, */ OnDeleteDefaults } from 'flow/editor/components/Graph/nodes/BaseNode/NodeDropdownMenu';
import {
  createWorkflowHistoryThunk,
  increaseActionPrecedenceThunk,
  setExistingPrecedenceIndexedActionAsDefaultThunk,
} from 'flow/store/redux/thunks';
import {
  getNormalizedOutcomeName,
  isDefaultPrecedenceIndexedAction,
  isActionOfHighestPrecedence,
} from 'flow/service/actions';
import type { NodeState } from 'flow/service/graph';
import type { WorkflowState } from 'flow/store/redux/state';

import { ActionType } from '~/dashboard/views/workflows/store/engine/constants';
import type { AppDispatch } from '~/store';

import { NormalizedOutcomeName } from './constants';
import { Operation } from 'flow/service/changelog/types';
import { createUiEffectContext } from 'flow/service/history';
import { changeDescription } from 'flow/store/redux/types';


export function getOutcomeStateStyle({
  outcomeAction,
  nodeState,
}: {
  outcomeAction: Action;
  nodeState: NodeState;
}): React.CSSProperties {
  const textColor = outcomeAction.outcome.color
    ? getTextColorForBackground(outcomeAction.outcome.color)
    : 'white';

  return {
    deemphasized: {
      color: textColor,
    },
    faded: {
      color: textColor,
    },
    floated: {
      color: textColor,
    },
    focused: {
      color: textColor,
    },
    'focused-hovered': {
      color: textColor,
    },
    highlighted: {
      color: textColor,
    },
    hovered: {
      backgroundColor: outcomeAction.outcome.color
        ? `#${outcomeAction.outcome.color}7f`
        : 'var(--cl-gray-600)',
      color: textColor,
    },
    normal: {
      color: textColor,
    },
  }[nodeState];
}

export function getOutcomeIconName(outcomeName: string): IconName {
  const normalizedOutcomeName = getNormalizedOutcomeName(outcomeName);

  if (normalizedOutcomeName === NormalizedOutcomeName.APPROVED) return IconName.Approved;
  if (normalizedOutcomeName === NormalizedOutcomeName.DENIED) return IconName.Xmark;
  if (normalizedOutcomeName === NormalizedOutcomeName.MANUAL_REVIEW) return IconName.Review;

  return IconName.CustomOutcome;
}

export function getDropdownItems({
  addSnackBar,
  dispatch,
  outcomeAction,
  outcomeActions,
  workflowData,
  setNodeDeletionChildActionCounts,
  onSwapOutcome,
  isWorkflowHistoryOn,
  // workflowProps,
}: {
  addSnackBar: ReturnType<typeof useSnackBar>;
  dispatch: AppDispatch;
  outcomeAction: Action;
  outcomeActions: Action[];
  workflowData: WorkflowState['workflowData'];
  workflowProps: WorkflowState['workflowProps'];
  onSwapOutcome: () => void;
  isWorkflowHistoryOn: boolean;
  setNodeDeletionChildActionCounts: React.Dispatch<React.SetStateAction<number>>;
}): DropdownItem[] {
  const { publicId: hoveredNodeId } = outcomeAction;
  const isTopMostOutcomeAction = isActionOfHighestPrecedence(
    workflowData.actions,
    outcomeAction,
    ActionType.OUTCOME,
  );

  const isDefault = isDefaultPrecedenceIndexedAction(outcomeAction);

  return [
    {
      label: 'Swap outcome',
      onSelect: onSwapOutcome,
      disabled: outcomeActions?.length <= 1,
    },
    {
      label: 'Increase precedence',
      onSelect: () => {
        dispatch(createWorkflowHistoryThunk(
          () => dispatch(increaseActionPrecedenceThunk(outcomeAction)),
          isWorkflowHistoryOn,
          {
            operation: Operation.Modify,
            uiEffectContext: createUiEffectContext(changeDescription.action,
              { publicId: outcomeAction.publicId }),
          },
        ));
      },
      disabled: isTopMostOutcomeAction || isDefault,
    },
    {
      label: 'Make default Outcome',
      onSelect: () => {
        dispatch(setExistingPrecedenceIndexedActionAsDefaultThunk(outcomeAction));
      },
      disabled: isDefault,
    },
    // hiding the option to copy at the node level until the feature is more stable
    // {
    //   label: 'Copy Outcome logic',
    //   onSelect: OnCopyDefaults.onSelect({
    //     addSnackBar,
    //     hoveredNodeId,
    //     workflowData,
    //     workflowProps,
    //   }),
    // },
    {
      disabled: (isDefault && outcomeActions?.length > 1),
      Icon: OnDeleteDefaults.Icon,
      label: OnDeleteDefaults.label,
      onSelect: OnDeleteDefaults.onSelect({
        addSnackBar,
        dispatch,
        isWorkflowHistoryOn,
        nodeId: hoveredNodeId,
        workflowData,
        setNodeDeletionChildActionCounts,
      }),
      style: OnDeleteDefaults.style,
    },
  ];
}
